import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AuthContextValue, AuthUser, LoginRequest } from '../types/authTypes';
import { loginAdmin } from '../services/authService';

const STORAGE_KEY = 'city_delights_auth';

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  });

  // Persist to localStorage whenever user changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const loginAsGuest = useCallback(() => {
    setUser({ username: 'אורח', role: 'guest', token: '' });
  }, []);

  const loginAsAdmin = useCallback(async (req: LoginRequest) => {
    const res = await loginAdmin(req);
    setUser({ username: res.username, role: 'admin', token: res.token });
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const value: AuthContextValue = {
    user,
    isAdmin: user?.role === 'admin',
    isGuest: user?.role === 'guest',
    loginAsGuest,
    loginAsAdmin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
